import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useShiftStore } from '@/store/shiftStore';
import ShiftStartForm from '@/components/pos/ShiftStartForm';
import { Clock, LogOut, PlayCircle } from 'lucide-react';

type Props = {
  onShiftStarted: () => void;
};

export default function ShiftRequiredPanel({ onShiftStarted }: Props) {
  const [showStartForm, setShowStartForm] = useState(false);
  const shiftJustClosed = useShiftStore((s) => s.shiftJustClosed);
  const setShiftJustClosed = useShiftStore((s) => s.setShiftJustClosed);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } finally {
      setShiftJustClosed(false);
      clearAuth();
      navigate('/login');
    }
  };

  if (showStartForm) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 p-6 min-w-0">
        <div className="w-full max-w-md">
          <ShiftStartForm
            onStarted={() => {
              setShowStartForm(false);
              onShiftStarted();
            }}
            onCancel={() => setShowStartForm(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50 p-6 min-w-0">
      <div className="card p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
          <Clock className="w-6 h-6 text-primary-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {shiftJustClosed ? 'Shift closed' : 'Shift required'}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {shiftJustClosed
            ? 'Your shift has ended. Start a new shift to continue selling, or log out.'
            : 'Open a shift with your cash drawer before using POS billing.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => setShowStartForm(true)}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            <PlayCircle className="w-4 h-4" /> Start Shift
          </button>
          <button
            type="button"
            onClick={() => { void handleLogout(); }}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>
    </div>
  );
}
